<template>
    <div 
        ref="dropzone"
        :id="id" 
        class="file-dropzone"
        v-bind="getRootProps()"
    >
        <input v-bind="getInputProps()" aria-label="Text input">
        <div v-if="!isDragActive" class="dz-message">
            <img src="/img/icons/avid-icons/arrow-circle-up.svg" alt="">
            <div class="dropzone-title">Drag and drop or click to upload</div>
            <!-- <div class="dropzone-subtitle">We recommend using files less than 20MB</div> -->
        </div>
    </div>
</template>

<script>
import { useDropzone } from 'vue3-dropzone';
import { defineComponent, ref, onMounted } from 'vue';

export default defineComponent({
    name: 'FileDropzone',
    props: {
        id: {
            type: String,
            default: 'dropzone'
        },
        clientUrl: {
            type: String,
            required: true
        },
        onFileUploaded: {
            type: Function,
            required: true
        },
        // New prop to determine upload method
        uploadMethod: {
            type: String,
            default: 'direct', // 'direct' or 'proxy'
            validator: (value) => ['direct', 'proxy'].includes(value)
        }
    },
    emits: ['fileUploading', 'uploadProgress', 'documentUploading', 'uploadError'],
    setup(props, { emit }) {
        const dropzone = ref(null);

        // Direct upload method (original FileDropzone)
        const uploadDirect = async (file) => {
            emit('fileUploading', file);
            
            // Get signed URL for GCP upload
            const response = await window.axios.post("/api/file_upload/get_signed_url", { 
                url: props.clientUrl, 
                file_name: Date.now() + "_" + file.name 
            });

            if (response.data.url) {
                // Upload file directly to GCP using the signed URL with progress tracking
                await window.axios.put(response.data.url, file, {
                    headers: {
                        'Content-Type': file.type
                    },
                    withCredentials: false,
                    onUploadProgress: (progressEvent) => {
                        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                        emit('uploadProgress', {
                            fileName: file.name,
                            progress: percentCompleted
                        });
                    }
                });

                // After successful upload to GCP, create file record in our system
                const formData = new FormData();
                formData.append("file_name", file.name);
                formData.append("gcs_path", response.data.url);
                formData.append("client_url", props.clientUrl);

                const uploadResponse = await window.axios.post('/api/file_upload/new_file', formData);
                
                // Call the callback with the uploaded file data
                props.onFileUploaded({
                    ...uploadResponse.data.file,
                    status: 'ready'
                });
            }
        };

        // Proxy upload method (original DocumentDropzone)
        const uploadProxy = async (file) => {
            emit('documentUploading', file);

            // Prepare form data for Laravel upload endpoint
            const formData = new FormData();
            formData.append("file", file);
            formData.append("url", props.clientUrl);

            // Upload file through Laravel proxy
            const response = await window.axios.post("/api/document/upload", formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                },
                onUploadProgress: (progressEvent) => {
                    const percentCompleted = Math.round(
                        (progressEvent.loaded * 100) / progressEvent.total
                    );
                    emit('uploadProgress', {
                        fileName: file.name,
                        progress: percentCompleted
                    });
                }
            });

            // Once Laravel confirms GCS upload, save file record
            const saveResponse = await window.axios.post('/api/document/save', {
                file_name: file.name,
                gcs_path: response.data.objectName,
                content_type: response.data.contentType,
                content_size: response.data.contentSize,
                usage: 'external',
                client_url: props.clientUrl
            });

            // Trigger callback
            props.onFileUploaded({
                ...saveResponse.data.document,
                status: 'ready'
            });
        };

        const onDrop = async (acceptedFiles) => {
            // Use for...of for proper async handling
            for (const file of acceptedFiles) {
                try {
                    if (props.uploadMethod === 'proxy') {
                        await uploadProxy(file);
                    } else {
                        await uploadDirect(file);
                    }
                } catch (error) {
                    const message = error.response?.data?.message || error.message || 'Upload failed. Please try again.';
                    console.error('Error uploading file:', error);
                    emit('uploadError', { file, message });
                }
            }
        };

        const dropzoneOptions = {
            onDrop,
            multiple: true,
            maxFilesize: 20, // MB
        };

        const { getRootProps, getInputProps, isDragActive, open } = useDropzone(dropzoneOptions);

        onMounted(() => {
            if (dropzone.value) {
                dropzone.value.addEventListener('click', open);
            }
        });

        return {
            dropzone,
            getRootProps,
            getInputProps,
            isDragActive
        };
    }
});
</script>
